import { FC, Fragment, useCallback, useEffect, useState } from 'react';
import { Button, Checkbox, Modal, Spin, Tooltip, message } from 'antd';
import close from '../../../../assets/RoundedClose.svg';
import done from '../../../../assets/Done, Check.svg';
import nextEaImage from '../../../../assets/Interrupted, Line, Circle.png';
import arrowIcon from '../../../../assets/arrow-right-white.svg';
import {
    useLazyGetSupplierProfilePreviewQuestionaryQuery,
    useSaveSupplierProfilePreviewFormMutation,
    useSyncSupplierProfilePreviewComponentsMutation
} from '../../../../api/expenseAreas';
import { Loader } from '../../../../components/Loader/Loader';
import { Arrow } from '../../../../components/Icons/Arrow';
import { ServiceComIcon } from '../../../../components/Icons/ServiceComIcon';
import InfoIcon from '../../../../components/Icons/infoIcon';
import { Section } from '../../../../api/types/expenseareas';
import {
    Dropdown,
    ExplanationBanner,
    MultipleChoice,
    Paragraph,
    ShortAnswer,
    SingleSelect
} from '../../../SignUp/fields/fieldsRender';
import '../../../../components/Modals/Modals.scss';
import '../../../SignUp/index.scss';
import './AdminSupplierProfilePreviewModal.scss';
import { PREVIEW_TOASTS } from './previewToastMessages';
import {
    ExpenseAreaForRender,
    ExpenseAreaSection,
    QuestionAnswer,
    applyQuestionaryServerErrors,
    applyStatusByLastSection,
    buildSaveDataFromExpenseArea,
    collectGoToSectionsFromQuestionary,
    extractApiErrorMessage,
    extractPreviewEaPayload,
    goToNextSectionState,
    goToPrevSectionState,
    isLastQuestionarySection,
    normalizeQuestionaryPayload,
    validateAnswers
} from './supplierProfilePreviewQuestionaryLogic';

interface Props {
    openModal: boolean;
    setOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
    expenseAreaId: number;
}

export const AdminSupplierProfilePreviewModal: FC<Props> = ({ openModal, setOpenModal, expenseAreaId }) => {
    const [getQuestionary, { isFetching: isLoadingQuestionary }] = useLazyGetSupplierProfilePreviewQuestionaryQuery();
    const [savePreview, { isLoading: isSavingPreview }] = useSaveSupplierProfilePreviewFormMutation();
    const [syncComponents, { isLoading: isLoadingSyncComponents }] = useSyncSupplierProfilePreviewComponentsMutation();

    const [list, setList] = useState<ExpenseAreaForRender[]>([]);
    const [previewPayload, setPreviewPayload] = useState<any>(null);
    const [activeEA, setActiveEA] = useState('');
    const [activeSectionId, setActiveSectionId] = useState('');
    const [expandedAreas, setExpandedAreas] = useState<number[]>([]);
    const [saveData, setSaveData] = useState<QuestionAnswer[]>([]);
    const [serverQuestionaryError, setServerQuestionaryError] = useState<string | null>(null);

    const EAData = list?.find((elem) => elem.expense_area_id + '' === activeEA + '');

    const loadQuestionary = useCallback(async (): Promise<ExpenseAreaForRender[]> => {
        try {
            const res = await getQuestionary({ expense_area_id: expenseAreaId }).unwrap();
            const raw = normalizeQuestionaryPayload(res, expenseAreaId);

            if (!raw.length) {
                message.error('Preview returned no questionnaire data.');
                setPreviewPayload(null);
                return [];
            }

            setPreviewPayload(extractPreviewEaPayload(res, expenseAreaId));

            const updatedData = raw.map((EA: any) => ({
                ...EA,
                formData: {
                    ...EA.formData,
                    sections: EA.formData?.sections?.filter((sec: Section) => !sec?.has_go_to) ?? []
                }
            }));
            const goToSections = collectGoToSectionsFromQuestionary(updatedData);
            const areasWithStatus = applyStatusByLastSection(raw, goToSections);
            setList(areasWithStatus);

            if (areasWithStatus.length) {
                const firstEA = areasWithStatus[0];
                setActiveEA(String(firstEA.expense_area_id));
                setActiveSectionId(`components-${firstEA.expense_area_id}`);
            }

            return areasWithStatus;
        } catch (error: unknown) {
            setPreviewPayload(null);
            message.error(extractApiErrorMessage(error, 'Could not load preview.'));
            return [];
        }
    }, [expenseAreaId, getQuestionary]);

    useEffect(() => {
        if (openModal && expenseAreaId) {
            loadQuestionary();
        }
    }, [openModal, expenseAreaId, loadQuestionary]);

    useEffect(() => {
        if (activeSectionId && previewPayload) {
            setSaveData(buildSaveDataFromExpenseArea(activeSectionId, previewPayload));
            setServerQuestionaryError(null);
        }
    }, [activeSectionId, previewPayload]);

    const toggleArea = (id: number) => {
        setExpandedAreas((prev) => (prev.includes(id) ? prev.filter((eid) => eid !== id) : [...prev, id]));
    };

    const updateAnswer = (id: number, value: any, type: string, component_id?: number) => {
        if (type === 'components') {
            setSaveData((prev) =>
                prev.map((item) => {
                    if (item.id === id) {
                        return {
                            ...item,
                            answer: item.answer === 'true' ? 'false' : 'true',
                            error: false,
                            errorMessage: undefined
                        };
                    }
                    return item;
                })
            );
            return;
        }

        setSaveData((prev) =>
            prev.map((item) => {
                const isMatch =
                    component_id != null ? item.id === id && item.component_id === component_id : item.id === id;

                if (!isMatch) return item;

                switch (type) {
                    case 'short-answer':
                    case 'paragraph':
                        return { ...item, answer: value, error: false, errorMessage: undefined };
                    case 'multiple-choice': {
                        const arr = Array.isArray(item.answer) ? item.answer : [];
                        const updated = arr.includes(value) ? arr.filter((v: any) => v !== value) : [...arr, value];
                        return { ...item, answer: updated, error: false, errorMessage: undefined };
                    }
                    case 'dropdown':
                        if (value === undefined || value === null) {
                            return { ...item, answer: '', error: false, errorMessage: undefined };
                        }
                        return { ...item, answer: value, error: false, errorMessage: undefined };
                    case 'single-select':
                        return {
                            ...item,
                            answer: item.answer === value ? '' : value,
                            error: false,
                            errorMessage: undefined
                        };
                    default:
                        return item;
                }
            })
        );
    };

    const refreshAfterSave = async () => {
        setServerQuestionaryError(null);
        message.success(PREVIEW_TOASTS.spqChangesSaved);
        const areasWithStatus = await loadQuestionary();

        const next = goToNextSectionState(areasWithStatus, activeEA, activeSectionId);
        if (next) {
            setActiveEA(next.activeEA);
            setActiveSectionId(next.activeSectionId);
            return;
        }

        if (isLastQuestionarySection(areasWithStatus, activeEA, activeSectionId)) {
            setOpenModal(false);
        }
    };

    const syncEAtoComponentsFunc = async () => {
        setServerQuestionaryError(null);
        try {
            await syncComponents({
                expense_area_id: expenseAreaId,
                data: {
                    selected_components: saveData?.filter((elem) => elem.answer === 'true')?.map((elem) => elem.id)
                }
            }).unwrap();
            await refreshAfterSave();
        } catch (error: any) {
            const payload = error?.data ?? {};
            const serverErrors = payload?.errors;
            if (serverErrors !== undefined && serverErrors !== null) {
                const { next, unmapped } = applyQuestionaryServerErrors(saveData, serverErrors);
                setSaveData(next);
                setServerQuestionaryError(unmapped.length ? unmapped.join('\n') : null);
            }
            message.error(extractApiErrorMessage(error, PREVIEW_TOASTS.spqSaveComponentsFailed));
        }
    };

    const handleSaveFields = async () => {
        setServerQuestionaryError(null);
        const { updated, isValid } = validateAnswers(saveData);

        if (!isValid) {
            setSaveData(updated);
            return;
        }

        const cleaned = saveData.map((item) => {
            if (!item || typeof item !== 'object') return item;

            const { required, error, errorMessage, ...rest } = item as QuestionAnswer & {
                required?: boolean;
                error?: boolean;
                errorMessage?: string;
            };

            if (rest.type === 'dropdown') {
                const ans = rest.answer;
                const answerForApi =
                    ans === '' || ans == null
                        ? []
                        : Array.isArray(ans)
                          ? ans.filter((v: any) => v != null && v !== '')
                          : [ans];
                return { ...rest, answer: answerForApi };
            }
            return rest;
        });

        try {
            await savePreview({
                expense_area_id: expenseAreaId,
                formData: {
                    section_id: activeSectionId,
                    fields: cleaned
                }
            }).unwrap();
            await refreshAfterSave();
        } catch (error: any) {
            const payload = error?.data ?? {};
            const serverErrors = payload?.errors;
            if (serverErrors !== undefined && serverErrors !== null) {
                const { next, unmapped } = applyQuestionaryServerErrors(saveData, serverErrors);
                setSaveData(next);
                setServerQuestionaryError(unmapped.length ? unmapped.join('\n') : null);
            } else {
                setServerQuestionaryError(null);
            }
            message.error(extractApiErrorMessage(error, PREVIEW_TOASTS.spqSaveAnswersFailed));
        }
    };

    const goToPrevSection = () => {
        const prev = goToPrevSectionState(list, activeEA, activeSectionId);
        if (prev) {
            setActiveEA(prev.activeEA);
            setActiveSectionId(prev.activeSectionId);
        }
    };

    const isInitialLoading = isLoadingQuestionary && !list.length;
    const isSectionLoading = isLoadingQuestionary && list.length > 0 && !saveData.length;

    return (
        <Modal
            className="questionary_modal admin-spq-preview-modal"
            destroyOnClose
            centered
            open={openModal}
            onCancel={() => setOpenModal(false)}
            closeIcon={<img src={close} alt="" />}
            footer={null}
        >
            {isInitialLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
                    <Spin />
                </div>
            ) : (
                <div className="signup admin-spq-preview-signup">
                    <div className="main">
                        <div className="main__questionary questionary">
                            <div className="questionary__header">
                                <div className="questionary__header-block-1">
                                    <div className="questionary__title">
                                        Expense Area Questionnaire{' '}
                                        <Tooltip title="Your responses to this questionnaire will form the basis of all Matchmaking Journey submissions for the relevant expense area. Please answer truthfully and accurately. You may be assessed against the claims made here, and supporting evidence may be requested. Misleading responses can result in disqualification from current or future opportunities.">
                                            <div>
                                                <InfoIcon fill="#808CB2" />
                                            </div>
                                        </Tooltip>
                                    </div>
                                </div>
                                <div className="questionary__header-block-2">
                                    Your questionnaire autosaves. You can return anytime to complete it or update your
                                    profile.
                                </div>
                            </div>

                            <div className="questionary__main">
                        <div className="questionary__EAs">
                            {list?.map((EA) => {
                                const isLastSectionId = EA.last_section_id;
                                const indexLastSectionId = EA?.sections.findIndex(
                                    (section: ExpenseAreaSection) => section.id === isLastSectionId + ''
                                );
                                const isSelectedComponents = EA.selected_components?.length;

                                if (!isLastSectionId && +EA.expense_area_id !== +activeEA) {
                                    return (
                                        <div className="questionary__nextarea nextarea" key={EA.expense_area_id}>
                                            <div>
                                                <img src={nextEaImage} alt="" />
                                                {EA?.name}
                                            </div>
                                            <Arrow />
                                        </div>
                                    );
                                }

                                const isExpanded = !expandedAreas.includes(EA.expense_area_id);

                                return (
                                    <div
                                        className={`questionary__area area ${isExpanded ? '' : 'expanded'}`}
                                        key={EA.expense_area_id}
                                    >
                                        {activeSectionId?.includes('components') &&
                                            EA.expense_area_id + '' === activeEA + '' && (
                                                <div className="area__helper-text">
                                                    Changing any answers may update or reset the following sections
                                                    based on your new inputs.
                                                </div>
                                            )}
                                        <div className="area__title">
                                            {EA?.name}{' '}
                                            <div onClick={() => toggleArea(EA.expense_area_id)}>
                                                <Arrow
                                                    fill="#001965"
                                                    style={isExpanded ? { transform: 'rotate(-180deg)' } : {}}
                                                />
                                            </div>
                                        </div>
                                        <div className={`area__sections ${isExpanded ? 'expanded' : ''}`}>
                                            {EA?.sections?.map((section: ExpenseAreaSection, index: number) => {
                                                const isComponents = section?.id?.includes('components');
                                                const isDone = isComponents ? isSelectedComponents : index <= indexLastSectionId;
                                                const isActive = section?.id + '' === activeSectionId + '';

                                                return (
                                                    <Fragment key={section.id}>
                                                        <div
                                                            className={`area__section ${
                                                                isActive ? 'active' : isDone ? 'done' : 'pending'
                                                            }`}
                                                        >
                                                            {isDone && section?.id + '' !== activeSectionId + '' ? (
                                                                <div className="number_of_section">
                                                                    <img src={done} alt="" />
                                                                </div>
                                                            ) : (
                                                                <div className="number_of_section">{index + 1}</div>
                                                            )}
                                                            {section?.name}
                                                        </div>
                                                        {index !== EA?.sections?.length - 1 && (
                                                            <div className="area__divider" />
                                                        )}
                                                    </Fragment>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <div className="questionary__fields-section fields-section">
                            {isSectionLoading ? (
                                <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
                                    <Spin />
                                </div>
                            ) : activeSectionId.includes('components') ? (
                                <div className="fields-section__components">
                                    <div className="fields-section__componentsHeader componentsHeader">
                                        <div className="componentsHeader__row">
                                            <div className="componentsHeader__title">
                                                <ServiceComIcon />
                                                Service Components
                                            </div>
                                            <div className="componentsHeader__section-number">
                                                Section 1 of {EAData?.sections?.length}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="fields-section__componentsMain componentsMain">
                                        <div className="componentsMain__title">
                                            Select all types of service components that you have for {EAData?.name}:
                                        </div>
                                        <div className="componentsMain__subtitle">
                                            You can select several options. Some of answers open an additional section of
                                            questions
                                        </div>
                                        {serverQuestionaryError && (
                                            <div
                                                className="error-text"
                                                style={{ margin: '0 0 12px 0', whiteSpace: 'pre-line' }}
                                            >
                                                {serverQuestionaryError}
                                            </div>
                                        )}
                                        <div className="componentsMain__questions">
                                            {saveData?.map((field) => (
                                                <div className="componentsMain__item" key={field.id}>
                                                    <Checkbox
                                                        onChange={() => updateAnswer(field.id, null, 'components')}
                                                        checked={field?.answer === 'true'}
                                                        className={field?.error ? 'red' : ''}
                                                    />{' '}
                                                    {field?.name}{' '}
                                                    <Tooltip title={field?.description}>
                                                        <div>
                                                            <InfoIcon fill="#808CB2" />
                                                        </div>
                                                    </Tooltip>
                                                    {field?.error && (
                                                        <div className="error-text" style={{ marginTop: 4 }}>
                                                            {field.errorMessage || 'Invalid selection'}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="fields-section__components">
                                    <div className="fields-section__componentsHeader componentsHeader">
                                        <div className="componentsHeader__row">
                                            <div className="componentsHeader__title">
                                                <ServiceComIcon />
                                                {
                                                    EAData?.sections?.find(
                                                        (item: ExpenseAreaSection) => item?.id + '' === activeSectionId + ''
                                                    )?.name
                                                }
                                            </div>
                                            <div className="componentsHeader__section-number">
                                                Section{' '}
                                                {(EAData?.sections?.findIndex(
                                                    (item: ExpenseAreaSection) => item?.id + '' === activeSectionId + ''
                                                ) ?? -1) + 1}{' '}
                                                of {EAData?.sections?.length}
                                            </div>
                                        </div>
                                        {EAData?.sections?.find(
                                            (item: ExpenseAreaSection) => item?.id + '' === activeSectionId + ''
                                        )?.description && (
                                            <div className="componentsHeader__row desc">
                                                {
                                                    EAData?.sections?.find(
                                                        (item: ExpenseAreaSection) =>
                                                            item?.id + '' === activeSectionId + ''
                                                    )?.description
                                                }
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        {serverQuestionaryError && (
                                            <div
                                                className="error-text"
                                                style={{ margin: '0 0 12px 0', whiteSpace: 'pre-line' }}
                                            >
                                                {serverQuestionaryError}
                                            </div>
                                        )}
                                        {saveData.map((field) => {
                                            const type = field?.type;

                                            if (type === 'short-answer') {
                                                return (
                                                    <ShortAnswer
                                                        key={`${field.id}-${field.component_id ?? 'n'}`}
                                                        field={field as any}
                                                        required={field.required}
                                                        updateAnswer={updateAnswer}
                                                        saveData={saveData as any}
                                                        component_id={null}
                                                    />
                                                );
                                            }
                                            if (type === 'paragraph') {
                                                return (
                                                    <Paragraph
                                                        key={`${field.id}-${field.component_id ?? 'n'}`}
                                                        field={field as any}
                                                        required={field.required}
                                                        updateAnswer={updateAnswer}
                                                        saveData={saveData as any}
                                                        component_id={null}
                                                    />
                                                );
                                            }
                                            if (type === 'dropdown') {
                                                return (
                                                    <Dropdown
                                                        key={`${field.id}-${field.component_id ?? 'n'}`}
                                                        field={field as any}
                                                        required={field.required}
                                                        updateAnswer={updateAnswer}
                                                        saveData={saveData as any}
                                                        component={field?.component_name}
                                                        component_id={field.component_id}
                                                    />
                                                );
                                            }
                                            if (type === 'single-select') {
                                                return (
                                                    <SingleSelect
                                                        key={`${field.id}-${field.component_id ?? 'n'}`}
                                                        field={field as any}
                                                        required={field.required}
                                                        updateAnswer={updateAnswer}
                                                        saveData={saveData as any}
                                                        component={field?.component_name}
                                                        component_id={field.component_id}
                                                    />
                                                );
                                            }
                                            if (type === 'multiple-choice') {
                                                return (
                                                    <MultipleChoice
                                                        key={`${field.id}-${field.component_id ?? 'n'}`}
                                                        field={field as any}
                                                        required={field.required}
                                                        updateAnswer={updateAnswer}
                                                        saveData={saveData as any}
                                                        component={field?.component_name}
                                                        component_id={field.component_id}
                                                    />
                                                );
                                            }
                                            if (type === 'explanation-banner') {
                                                return (
                                                    <ExplanationBanner
                                                        key={`${field.id}-${field.component_id ?? 'n'}`}
                                                        field={field as any}
                                                        required={field.required}
                                                        saveData={saveData as any}
                                                        component_id={null}
                                                    />
                                                );
                                            }
                                            return null;
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="questionary__btns">
                        {activeSectionId?.includes('components') ? (
                            list.findIndex((elem) => elem.expense_area_id + '' === activeEA + '') === 0 ? (
                                <>
                                    <div />
                                    <Button
                                        type="primary"
                                        onClick={syncEAtoComponentsFunc}
                                        disabled={saveData?.every((elem) => elem.answer === 'false')}
                                    >
                                        {isLoadingSyncComponents ? (
                                            <Loader />
                                        ) : (
                                            <div className="questionary__btn">
                                                Next Step <img src={arrowIcon} alt="" />
                                            </div>
                                        )}
                                    </Button>
                                </>
                            ) : (
                                <>
                                    <Button onClick={goToPrevSection}>Previous</Button>
                                    <Button
                                        type="primary"
                                        onClick={syncEAtoComponentsFunc}
                                        disabled={saveData?.every((elem) => elem.answer === 'false')}
                                    >
                                        {isLoadingSyncComponents ? (
                                            <Loader />
                                        ) : (
                                            <div className="questionary__btn">
                                                Next Step <img src={arrowIcon} alt="" />
                                            </div>
                                        )}
                                    </Button>
                                </>
                            )
                        ) : (
                            <>
                                <Button onClick={goToPrevSection}>Previous</Button>
                                <Button type="primary" onClick={handleSaveFields}>
                                    {isSavingPreview ? (
                                        <Loader />
                                    ) : (
                                        <div className="questionary__btn">
                                            Next Step <img src={arrowIcon} alt="" />
                                        </div>
                                    )}
                                </Button>
                            </>
                        )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </Modal>
    );
};
